// Size chart zoom: clicking the size chart image (custom.custom_size_chart_image,
// rendered as img.product__size-chart-image) opens a full-screen viewer.
//   - opens fitted to the screen
//   - click the image: zoom to 2x the fitted size, centred on the point clicked
//     (scroll / drag to pan); click again to fit back
//   - close: x button, Esc, or a click on the empty background
// Works with section re-renders (variant changes) via event delegation.
(() => {
  const SELECTOR = 'img.product__size-chart-image';
  const ZOOM = 2;
  let viewer = null;
  let stage = null;
  let img = null;
  let lastFocus = null;
  let zoomed = false;
  let drag = null;

  function fullSrc(source) {
    const raw = source.currentSrc || source.src || '';
    try {
      const url = new URL(raw, location.href);
      url.searchParams.set('width', '3000');
      return url.toString();
    } catch (e) {
      return raw;
    }
  }

  function build() {
    if (viewer) return;
    viewer = document.createElement('div');
    viewer.className = 'scz';
    viewer.setAttribute('role', 'dialog');
    viewer.setAttribute('aria-modal', 'true');
    viewer.setAttribute('aria-label', 'Size chart');
    viewer.hidden = true;
    viewer.innerHTML =
      '<button type="button" class="scz__close" aria-label="Close">' +
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5 L19 19 M19 5 L5 19" /></svg>' +
      '</button>' +
      '<div class="scz__stage"><img class="scz__img" alt="" draggable="false"></div>';
    document.body.appendChild(viewer);
    stage = viewer.querySelector('.scz__stage');
    img = viewer.querySelector('.scz__img');

    viewer.querySelector('.scz__close').addEventListener('click', close);

    // Background click closes (but not the end of a drag).
    stage.addEventListener('click', (event) => {
      if (event.target === stage && !(drag && drag.moved)) close();
    });

    img.addEventListener('click', (event) => {
      if (drag && drag.moved) return;
      toggleZoom(event);
    });

    // Drag to pan when zoomed (mouse).
    stage.addEventListener('pointerdown', (event) => {
      if (!zoomed || event.pointerType !== 'mouse' || event.button !== 0) return;
      drag = { x: event.clientX, y: event.clientY, left: stage.scrollLeft, top: stage.scrollTop, moved: false };
      stage.classList.add('is-dragging');
    });
    window.addEventListener('pointermove', (event) => {
      if (!drag) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (Math.abs(dx) + Math.abs(dy) > 4) drag.moved = true;
      stage.scrollLeft = drag.left - dx;
      stage.scrollTop = drag.top - dy;
    });
    window.addEventListener('pointerup', () => {
      if (!drag) return;
      stage.classList.remove('is-dragging');
      // keep "moved" for the click that follows this pointerup, then reset
      setTimeout(() => { drag = null; }, 0);
    });

    document.addEventListener('keydown', (event) => {
      if (viewer.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      } else if (event.key === 'Tab') {
        // only one focusable control: keep focus inside the dialog
        event.preventDefault();
        viewer.querySelector('.scz__close').focus();
      }
    });
  }

  function unzoom() {
    zoomed = false;
    viewer.classList.remove('is-zoomed');
    img.style.width = '';
    stage.scrollTo(0, 0);
  }

  function toggleZoom(event) {
    if (zoomed) return unzoom();
    const r = img.getBoundingClientRect();
    const fx = r.width ? Math.min(1, Math.max(0, (event.clientX - r.left) / r.width)) : 0.5;
    const fy = r.height ? Math.min(1, Math.max(0, (event.clientY - r.top) / r.height)) : 0.5;
    zoomed = true;
    img.style.width = Math.round(r.width * ZOOM) + 'px';
    viewer.classList.add('is-zoomed');
    // centre the clicked point
    requestAnimationFrame(() => {
      stage.scrollLeft = Math.max(0, img.offsetLeft + img.offsetWidth * fx - stage.clientWidth / 2);
      stage.scrollTop = Math.max(0, img.offsetTop + img.offsetHeight * fy - stage.clientHeight / 2);
    });
  }

  function open(source) {
    build();
    lastFocus = document.activeElement;
    img.alt = source.alt || 'Size chart';
    img.src = fullSrc(source);
    viewer.hidden = false;
    unzoom();
    document.documentElement.classList.add('scz-lock');
    requestAnimationFrame(() => viewer.classList.add('is-open'));
    viewer.querySelector('.scz__close').focus({ preventScroll: true });
  }

  function close() {
    if (!viewer || viewer.hidden) return;
    unzoom();
    viewer.classList.remove('is-open');
    viewer.hidden = true;
    document.documentElement.classList.remove('scz-lock');
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true });
  }

  // Make each size chart image keyboard-reachable.
  function prepare(root) {
    (root || document).querySelectorAll(SELECTOR).forEach((el) => {
      if (el.dataset.sczReady) return;
      el.dataset.sczReady = '1';
      el.setAttribute('role', 'button');
      el.setAttribute('tabindex', '0');
      el.setAttribute('aria-label', (el.alt || 'Size chart') + ' - click to enlarge');
    });
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest(SELECTOR) : null;
    if (!target) return;
    event.preventDefault();
    open(target);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const target = event.target instanceof Element ? event.target.closest(SELECTOR) : null;
    if (!target) return;
    event.preventDefault();
    open(target);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => prepare());
  else prepare();
  new MutationObserver(() => prepare()).observe(document.body, { childList: true, subtree: true });
})();
